import { ComplianceCaseTypeEnum, RiskRating } from "~/graphql/supergraphEnums";
import {
  type ComplianceCaseTypeEnum as ComplianceCaseType,
  type Program,
  type RiskRating as RiskRatingType,
} from "~/graphql/supergraphTypes";
import { isEntityClientOnboardingCaseType } from "../../ComplianceTable/CaseTypeRenderer/utils";
import { shouldShowRiskRating } from "../../utils";
import { getInvestingProfileName } from "../Profiles/utils";

type HeaderProgram = Pick<Program, "id" | "name">;

type CaseDisplayNameFields = {
  caseType?: ComplianceCaseType | null;
  name?: string | null;
  externalEntity?: { name?: string | null } | null;
  investingProfile?: Parameters<typeof getInvestingProfileName>[0];
};

type HeaderTitleCase = CaseDisplayNameFields & {
  externalArena?: { name?: string | null } | null;
};

export const getEntityOnboardingDisplayName = (
  complianceCase: Pick<CaseDisplayNameFields, "investingProfile" | "externalEntity">,
): string => {
  const profileName = getInvestingProfileName(complianceCase.investingProfile);
  if (profileName !== "--") {
    return profileName;
  }
  return complianceCase.externalEntity?.name || "--";
};

export const getCaseListDisplayName = (complianceCase: CaseDisplayNameFields): string => {
  if (complianceCase.caseType === ComplianceCaseTypeEnum.EntityOnboarding) {
    return getEntityOnboardingDisplayName(complianceCase);
  }
  return complianceCase.name || getInvestingProfileName(complianceCase.investingProfile);
};

export const getHeaderTitle = (
  complianceCase: HeaderTitleCase | null | undefined,
  usesProfiles: boolean,
): string => {
  if (!complianceCase) {
    return "--";
  }

  if (complianceCase.caseType === ComplianceCaseTypeEnum.ClientOnboarding) {
    return (
      complianceCase.externalArena?.name ||
      getInvestingProfileName(complianceCase.investingProfile)
    );
  }

  if (complianceCase.caseType === ComplianceCaseTypeEnum.EntityOnboarding) {
    return getEntityOnboardingDisplayName(complianceCase);
  }

  if (complianceCase.name) {
    return complianceCase.name;
  }

  return usesProfiles ? getInvestingProfileName(complianceCase.investingProfile) : "--";
};

export const isVisibleSubtitleDetail = (
  detail: string | number | null | undefined,
): boolean => {
  if (detail === "Position" || detail === "Escalation") return true;
  if (typeof detail === "number") return true;
  return typeof detail === "string" && detail.trim() !== "" && detail !== "--";
};

export const getHeaderCasePrograms = (
  caseType: ComplianceCaseTypeEnum | null | undefined,
  casePrograms: HeaderProgram[] | null | undefined,
  program: HeaderProgram | null | undefined,
): HeaderProgram[] => {
  if (casePrograms && casePrograms.length > 0) {
    return casePrograms;
  }
  if (caseType && isEntityClientOnboardingCaseType(caseType) && program?.name) {
    return [program];
  }
  return [];
};

/**
 * This is a wrapper utility to find the case level risk rating.
 * If the case level risk rating is not available, it will use the profile risk rating.
 * If the profile risk rating is not available, it will return None.
 */
export const getDisplayRiskRating = (
  programId?: number,
  caseRiskRating?: RiskRatingType | null,
  profileRiskRating?: RiskRatingType | null,
): RiskRatingType => {
  if (programId === undefined || !shouldShowRiskRating(programId)) {
    return RiskRating.None;
  }
  if (caseRiskRating) {
    return caseRiskRating;
  }
  return profileRiskRating ?? RiskRating.None;
};
